import { useCallback, useEffect } from 'react';
import { useCanvasStore } from '@/stores/canvas.store';
import { NodeType, NodeStatus, NODE_TYPE_COLORS } from '@/types/node.types';
import type { Priority } from '@/types/node.types';

export function useCanvas(canvasId: string | undefined) {
  const {
    canvas,
    nodes,
    edges,
    isLoading,
    error,
    loadCanvas,
    clearCanvas,
    onNodesChange,
    onEdgesChange,
    onConnect,
    addNode,
    updateNode,
    removeNode,
    addEdge,
    updateEdge,
    removeEdge,
  } = useCanvasStore();

  useEffect(() => {
    if (canvasId) {
      loadCanvas(canvasId);
    }
    return () => {
      clearCanvas();
    };
  }, [canvasId, loadCanvas, clearCanvas]);

  const createNode = useCallback(
    async (
      nodeType: NodeType,
      position: { x: number; y: number },
      title: string = `Nouveau ${nodeType}`,
      priority: Priority = 3,
    ) => {
      if (!canvasId) return;
      return addNode({
        title,
        type: nodeType,
        canvasId,
        positionX: position.x,
        positionY: position.y,
        priority,
        status: NodeStatus.Active,
      });
    },
    [canvasId, addNode],
  );

  const getNodeColor = useCallback((nodeType: NodeType) => {
    return NODE_TYPE_COLORS[nodeType];
  }, []);

  return {
    canvas,
    nodes,
    edges,
    isLoading,
    error,
    onNodesChange,
    onEdgesChange,
    onConnect,
    createNode,
    updateNode,
    removeNode,
    addEdge,
    updateEdge,
    removeEdge,
    getNodeColor,
  };
}

export default useCanvas;
